import { getAuth } from 'firebase/auth';
import { Achievement } from '../../types/goals';
import { createAchievement, hasAchievement, getAchievement } from './achievement.service';
import { getTrainings } from './training.service';
import { getUserCompetitionStats } from './competition.service';
import { goalsService } from '../goals.service';
import logger from '../../utils/logger';

/**
 * Service for managing achievement triggers and checking for new achievements
 * This service handles the logic for when achievements should be unlocked
 */

/**
 * Types of achievements that can be triggered
 */
export enum AchievementType {
  // Training related
  FIRST_TRAINING = 'first_training',
  TRAINING_STREAK = 'training_streak',
  TRAINING_COUNT = 'training_count',
  TRAINING_HOURS = 'training_hours',
  LOCATIONS_VISITED = 'locations_visited',
  
  // Competition related
  FIRST_COMPETITION = 'first_competition',
  COMPETITION_COUNT = 'competition_count',
  MEDAL_COUNT = 'medal_count',
  GOLD_MEDAL_COUNT = 'gold_medal_count',
  
  // Belt progression related
  BELT_PROMOTION = 'belt_promotion',
  REACH_BELT_RANK = 'reach_belt_rank',
  YEARS_TRAINING = 'years_training',
  
  // Goals related
  FIRST_GOAL = 'first_goal',
  GOALS_COMPLETED = 'goals_completed',
  GOAL_STREAK = 'goal_streak',
  
  // Social related
  FRIEND_COUNT = 'friend_count',
  POSTS_COUNT = 'posts_count',
  COMMENTS_COUNT = 'comments_count',
  LIKES_RECEIVED = 'likes_received',
  
  // Special
  PROFILE_COMPLETE = 'profile_complete',
  APP_USAGE = 'app_usage'
}

/**
 * Achievement categories to organize achievements
 */
export enum AchievementCategory {
  TRAINING = 'training',
  COMPETITION = 'competition',
  BELT = 'belt',
  GOALS = 'goals',
  SOCIAL = 'social',
  SPECIAL = 'special'
}

/**
 * Achievement level (for tiered achievements)
 */
export enum AchievementLevel {
  BRONZE = 1,
  SILVER = 2,
  GOLD = 3,
  PLATINUM = 4
}

/**
 * Achievement definition with thresholds for different levels
 */
interface AchievementDefinition {
  type: AchievementType;
  category: AchievementCategory;
  name: string;
  description: string;
  requirement: string;
  points: number;
  thresholds?: {
    [AchievementLevel.BRONZE]?: number;
    [AchievementLevel.SILVER]?: number;
    [AchievementLevel.GOLD]?: number;
    [AchievementLevel.PLATINUM]?: number;
  };
  iconUrl?: string;
}

/**
 * All possible achievements in the system
 */
const ACHIEVEMENTS: AchievementDefinition[] = [
  // Training achievements
  {
    type: AchievementType.FIRST_TRAINING,
    category: AchievementCategory.TRAINING,
    name: 'First Session',
    description: 'Logged your first training session',
    requirement: 'Log a training session',
    points: 10
  },
  {
    type: AchievementType.TRAINING_STREAK,
    category: AchievementCategory.TRAINING,
    name: 'Training Streak',
    description: 'Train consistently for a streak of days',
    requirement: 'Train for consecutive days',
    points: 25,
    thresholds: {
      [AchievementLevel.BRONZE]: 3,   // 3 days
      [AchievementLevel.SILVER]: 7,   // 7 days
      [AchievementLevel.GOLD]: 14,    // 14 days
      [AchievementLevel.PLATINUM]: 30 // 30 days
    }
  },
  {
    type: AchievementType.TRAINING_COUNT,
    category: AchievementCategory.TRAINING,
    name: 'Training Milestone',
    description: 'Reach a certain number of training sessions',
    requirement: 'Log multiple training sessions',
    points: 50,
    thresholds: {
      [AchievementLevel.BRONZE]: 10,    // 10 sessions
      [AchievementLevel.SILVER]: 50,    // 50 sessions
      [AchievementLevel.GOLD]: 100,     // 100 sessions
      [AchievementLevel.PLATINUM]: 500  // 500 sessions
    }
  },
  {
    type: AchievementType.TRAINING_HOURS,
    category: AchievementCategory.TRAINING,
    name: 'Training Hours',
    description: 'Train for a certain number of hours',
    requirement: 'Accumulate training hours',
    points: 75,
    thresholds: {
      [AchievementLevel.BRONZE]: 10,    // 10 hours
      [AchievementLevel.SILVER]: 50,    // 50 hours
      [AchievementLevel.GOLD]: 100,     // 100 hours
      [AchievementLevel.PLATINUM]: 500  // 500 hours
    }
  },
  {
    type: AchievementType.LOCATIONS_VISITED,
    category: AchievementCategory.TRAINING,
    name: 'Training Explorer',
    description: 'Train at multiple different locations',
    requirement: 'Train at different gyms or locations',
    points: 30,
    thresholds: {
      [AchievementLevel.BRONZE]: 3,   // 3 locations
      [AchievementLevel.SILVER]: 5,   // 5 locations
      [AchievementLevel.GOLD]: 10,    // 10 locations
      [AchievementLevel.PLATINUM]: 25 // 25 locations
    }
  },
  
  // Competition achievements
  {
    type: AchievementType.FIRST_COMPETITION,
    category: AchievementCategory.COMPETITION,
    name: 'First Competition',
    description: 'Participated in your first competition',
    requirement: 'Log a competition',
    points: 25
  },
  {
    type: AchievementType.COMPETITION_COUNT,
    category: AchievementCategory.COMPETITION,
    name: 'Competition Veteran',
    description: 'Participate in multiple competitions',
    requirement: 'Log multiple competitions',
    points: 50,
    thresholds: {
      [AchievementLevel.BRONZE]: 3,   // 3 competitions
      [AchievementLevel.SILVER]: 5,   // 5 competitions
      [AchievementLevel.GOLD]: 10,    // 10 competitions
      [AchievementLevel.PLATINUM]: 25 // 25 competitions
    }
  },
  {
    type: AchievementType.MEDAL_COUNT,
    category: AchievementCategory.COMPETITION,
    name: 'Medal Collector',
    description: 'Win medals in competitions',
    requirement: 'Win medals in competitions',
    points: 75,
    thresholds: {
      [AchievementLevel.BRONZE]: 1,  // 1 medal
      [AchievementLevel.SILVER]: 5,  // 5 medals
      [AchievementLevel.GOLD]: 10,   // 10 medals
      [AchievementLevel.PLATINUM]: 25 // 25 medals
    }
  },
  {
    type: AchievementType.GOLD_MEDAL_COUNT,
    category: AchievementCategory.COMPETITION,
    name: 'Gold Standard',
    description: 'Win gold medals in competitions',
    requirement: 'Win gold medals in competitions',
    points: 100,
    thresholds: {
      [AchievementLevel.BRONZE]: 1,  // 1 gold medal
      [AchievementLevel.SILVER]: 3,  // 3 gold medals
      [AchievementLevel.GOLD]: 5,    // 5 gold medals
      [AchievementLevel.PLATINUM]: 10 // 10 gold medals
    }
  },
  
  // Belt progression achievements
  {
    type: AchievementType.BELT_PROMOTION,
    category: AchievementCategory.BELT,
    name: 'Belt Milestone',
    description: 'Promoted to a new belt rank',
    requirement: 'Record a belt promotion',
    points: 50,
    thresholds: {
      [AchievementLevel.BRONZE]: 1,  // First promotion
      [AchievementLevel.SILVER]: 3,  // 3 promotions
      [AchievementLevel.GOLD]: 5,    // 5 promotions
      [AchievementLevel.PLATINUM]: 8  // 8 promotions
    }
  },
  
  // Goals achievements
  {
    type: AchievementType.FIRST_GOAL,
    category: AchievementCategory.GOALS,
    name: 'Goal Setter',
    description: 'Set your first training goal',
    requirement: 'Create a training goal',
    points: 10
  },
  {
    type: AchievementType.GOALS_COMPLETED,
    category: AchievementCategory.GOALS,
    name: 'Goal Achiever',
    description: 'Complete multiple training goals',
    requirement: 'Complete training goals',
    points: 50,
    thresholds: {
      [AchievementLevel.BRONZE]: 1,   // 1 goal
      [AchievementLevel.SILVER]: 5,   // 5 goals
      [AchievementLevel.GOLD]: 10,    // 10 goals
      [AchievementLevel.PLATINUM]: 25  // 25 goals
    }
  },
  
  // Social achievements
  {
    type: AchievementType.FRIEND_COUNT,
    category: AchievementCategory.SOCIAL,
    name: 'Connected',
    description: 'Connect with other martial artists',
    requirement: 'Add friends on Marune',
    points: 25,
    thresholds: {
      [AchievementLevel.BRONZE]: 1,   // 1 friend
      [AchievementLevel.SILVER]: 10,  // 10 friends
      [AchievementLevel.GOLD]: 25,    // 25 friends
      [AchievementLevel.PLATINUM]: 50  // 50 friends
    }
  },
  {
    type: AchievementType.POSTS_COUNT,
    category: AchievementCategory.SOCIAL,
    name: 'Contributor',
    description: 'Share your training journey',
    requirement: 'Create posts on Marune',
    points: 30,
    thresholds: {
      [AchievementLevel.BRONZE]: 1,   // 1 post
      [AchievementLevel.SILVER]: 10,  // 10 posts
      [AchievementLevel.GOLD]: 25,    // 25 posts
      [AchievementLevel.PLATINUM]: 100 // 100 posts
    }
  },
  
  // Special achievements
  {
    type: AchievementType.PROFILE_COMPLETE,
    category: AchievementCategory.SPECIAL,
    name: 'Identity Established',
    description: 'Complete your Marune profile',
    requirement: 'Fill out all profile fields',
    points: 15
  },
  {
    type: AchievementType.APP_USAGE,
    category: AchievementCategory.SPECIAL,
    name: 'Dedicated Martial Artist',
    description: 'Use Marune consistently',
    requirement: 'Log into Marune consistently',
    points: 25,
    thresholds: {
      [AchievementLevel.BRONZE]: 7,    // 7 days
      [AchievementLevel.SILVER]: 30,   // 30 days
      [AchievementLevel.GOLD]: 100,    // 100 days
      [AchievementLevel.PLATINUM]: 365  // 365 days
    }
  }
];

/**
 * Get achievement definition by type
 * @param type Achievement type to get
 * @returns Achievement definition or undefined if not found
 */
const getAchievementDefinition = (type: AchievementType): AchievementDefinition | undefined => {
  return ACHIEVEMENTS.find(a => a.type === type);
};

/**
 * Creates a new achievement and returns it
 * @param type Achievement type to create
 * @param level Achievement level (for tiered achievements)
 * @param relatedId ID of related item (e.g., goal ID, training ID, etc.)
 */
export const createUserAchievement = async (
  type: AchievementType, 
  level: AchievementLevel = AchievementLevel.BRONZE,
  relatedId?: string
): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      throw new Error('You must be logged in to create an achievement');
    }
    
    // Get achievement definition
    const definition = getAchievementDefinition(type);
    if (!definition) {
      throw new Error(`Achievement definition not found for type: ${type}`);
    }
    
    // Check if achievement already exists for this user
    const alreadyHasAchievement = await hasAchievement(type, definition.category);
    if (alreadyHasAchievement) {
      // If it's a tiered achievement, we'll update the level later
      if (!definition.thresholds) {
        // Not a tiered achievement, so we don't need to create it again
        return null;
      }
    }
    
    // Calculate points based on level for tiered achievements
    let pointsForLevel = definition.points;
    if (definition.thresholds) {
      // Higher level = more points
      pointsForLevel = definition.points * (level / AchievementLevel.BRONZE);
    }

    // Create achievement data - IMPORTANT: Only include defined values
    // Firestore throws "invalid-argument" error when trying to save undefined values
    const achievementData: Omit<Achievement, 'id' | 'dateEarned'> = {
      userId: user.uid,
      name: definition.name,
      category: definition.category,
      isPublic: true,
      points: Math.round(pointsForLevel),
      achievementType: type,
      level,
      isNew: true,
      shareCount: 0
    };

    // Only add optional fields if they have values (Firestore rejects undefined)
    if (definition.description) {
      achievementData.description = definition.description;
    }
    if (definition.requirement) {
      achievementData.requirementDescription = definition.requirement;
    }
    if (definition.iconUrl) {
      achievementData.iconUrl = definition.iconUrl;
    }
    if (relatedId) {
      achievementData.relatedGoalId = relatedId;
    }

    // Create achievement in Firebase (RTDB)
    const achievementId = await createAchievement(achievementData);

    // Return the created achievement
    return await getAchievement(achievementId);
  } catch (error) {
    logger.error('Error creating achievement:', error);
    return null;
  }
};

/**
 * Checks for the training streak achievement
 * Triggered when a user logs a new training session
 */
export const checkTrainingStreakAchievement = async (): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }
    
    // Get user's training sessions (RTDB), then extract dates locally
    const trainings = await getTrainings(user.uid);

    // Extract dates and sort them
    const dates: Date[] = [];
    trainings.forEach(training => {
      if (training.date) {
        dates.push(new Date(training.date));
      }
    });
    
    // Sort dates newest to oldest
    dates.sort((a, b) => b.getTime() - a.getTime());
    
    // Calculate streak
    let streak = 1; // Start with 1 for today
    let currentDate = new Date(dates[0]);
    
    for (let i = 1; i < dates.length; i++) {
      const previousDate = new Date(dates[i]);
      
      // Check if dates are consecutive
      const dayDiff = Math.floor(
        (currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (dayDiff === 1) {
        // Consecutive day
        streak++;
        currentDate = previousDate;
      } else if (dayDiff === 0) {
        // Same day - don't count as streak, but update current date
        currentDate = previousDate;
      } else {
        // Streak broken
        break;
      }
    }
    
    // Determine achievement level based on streak
    const streakDef = getAchievementDefinition(AchievementType.TRAINING_STREAK);
    if (!streakDef || !streakDef.thresholds) {
      return null;
    }
    
    // Check if streak qualifies for an achievement
    let achievementLevel: AchievementLevel | null = null;
    
    if (streak >= streakDef.thresholds[AchievementLevel.PLATINUM]!) {
      achievementLevel = AchievementLevel.PLATINUM;
    } else if (streak >= streakDef.thresholds[AchievementLevel.GOLD]!) {
      achievementLevel = AchievementLevel.GOLD;
    } else if (streak >= streakDef.thresholds[AchievementLevel.SILVER]!) {
      achievementLevel = AchievementLevel.SILVER;
    } else if (streak >= streakDef.thresholds[AchievementLevel.BRONZE]!) {
      achievementLevel = AchievementLevel.BRONZE;
    }
    
    if (achievementLevel) {
      return await createUserAchievement(
        AchievementType.TRAINING_STREAK,
        achievementLevel
      );
    }
    
    return null;
  } catch (error) {
    logger.error('Error checking training streak achievement:', error);
    return null;
  }
};

/**
 * Checks for training count achievements
 * Triggered when a user logs a new training session
 */
export const checkTrainingCountAchievement = async (): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }
    
    // Get count of user's training sessions (RTDB)
    const trainings = await getTrainings(user.uid);
    const trainingCount = trainings.length;
    
    // Determine achievement level based on count
    const countDef = getAchievementDefinition(AchievementType.TRAINING_COUNT);
    if (!countDef || !countDef.thresholds) {
      return null;
    }
    
    // Check if count qualifies for an achievement
    let achievementLevel: AchievementLevel | null = null;
    
    if (trainingCount >= countDef.thresholds[AchievementLevel.PLATINUM]!) {
      achievementLevel = AchievementLevel.PLATINUM;
    } else if (trainingCount >= countDef.thresholds[AchievementLevel.GOLD]!) {
      achievementLevel = AchievementLevel.GOLD;
    } else if (trainingCount >= countDef.thresholds[AchievementLevel.SILVER]!) {
      achievementLevel = AchievementLevel.SILVER;
    } else if (trainingCount >= countDef.thresholds[AchievementLevel.BRONZE]!) {
      achievementLevel = AchievementLevel.BRONZE;
    }
    
    if (achievementLevel) {
      return await createUserAchievement(
        AchievementType.TRAINING_COUNT,
        achievementLevel
      );
    }
    
    return null;
  } catch (error) {
    logger.error('Error checking training count achievement:', error);
    return null;
  }
};

/**
 * Checks if user has completed their first training session
 * Triggered when a user logs a new training session
 */
export const checkFirstTrainingAchievement = async (): Promise<Achievement | null> => {
  try {
    // Check if user already has this achievement
    const alreadyHasAchievement = await hasAchievement(
      AchievementType.FIRST_TRAINING,
      AchievementCategory.TRAINING
    );
    
    if (alreadyHasAchievement) {
      return null;
    }
    
    // Create the achievement
    return await createUserAchievement(AchievementType.FIRST_TRAINING);
  } catch (error) {
    logger.error('Error checking first training achievement:', error);
    return null;
  }
};

/**
 * Checks for competition-related achievements
 * Triggered when a user logs a new competition
 */
export const checkCompetitionAchievements = async (): Promise<Achievement[]> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return [];
    }
    
    const achievements: Achievement[] = [];
    
    // Check for first competition achievement
    const firstCompetition = await checkFirstCompetitionAchievement();
    if (firstCompetition) {
      achievements.push(firstCompetition);
    }
    
    // Check for competition count achievement
    const competitionCount = await checkCompetitionCountAchievement();
    if (competitionCount) {
      achievements.push(competitionCount);
    }
    
    // Check for medal achievements
    const medalCount = await checkMedalCountAchievement();
    if (medalCount) {
      achievements.push(medalCount);
    }
    
    const goldMedalCount = await checkGoldMedalCountAchievement();
    if (goldMedalCount) {
      achievements.push(goldMedalCount);
    }
    
    return achievements;
  } catch (error) {
    logger.error('Error checking competition achievements:', error);
    return [];
  }
};

/**
 * Checks if user has logged their first competition
 */
const checkFirstCompetitionAchievement = async (): Promise<Achievement | null> => {
  try {
    // Check if user already has this achievement
    const alreadyHasAchievement = await hasAchievement(
      AchievementType.FIRST_COMPETITION,
      AchievementCategory.COMPETITION
    );
    
    if (alreadyHasAchievement) {
      return null;
    }
    
    // Create the achievement
    return await createUserAchievement(AchievementType.FIRST_COMPETITION);
  } catch (error) {
    logger.error('Error checking first competition achievement:', error);
    return null;
  }
};

/**
 * Checks for competition count achievements
 */
const checkCompetitionCountAchievement = async (): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }
    
    // Get count of user's competitions (RTDB)
    const stats = await getUserCompetitionStats(user.uid);
    const competitionCount = stats.totalCompetitions;
    
    // Determine achievement level based on count
    const countDef = getAchievementDefinition(AchievementType.COMPETITION_COUNT);
    if (!countDef || !countDef.thresholds) {
      return null;
    }
    
    // Check if count qualifies for an achievement
    let achievementLevel: AchievementLevel | null = null;
    
    if (competitionCount >= countDef.thresholds[AchievementLevel.PLATINUM]!) {
      achievementLevel = AchievementLevel.PLATINUM;
    } else if (competitionCount >= countDef.thresholds[AchievementLevel.GOLD]!) {
      achievementLevel = AchievementLevel.GOLD;
    } else if (competitionCount >= countDef.thresholds[AchievementLevel.SILVER]!) {
      achievementLevel = AchievementLevel.SILVER;
    } else if (competitionCount >= countDef.thresholds[AchievementLevel.BRONZE]!) {
      achievementLevel = AchievementLevel.BRONZE;
    }
    
    if (achievementLevel) {
      return await createUserAchievement(
        AchievementType.COMPETITION_COUNT,
        achievementLevel
      );
    }
    
    return null;
  } catch (error) {
    logger.error('Error checking competition count achievement:', error);
    return null;
  }
};

/**
 * Checks for medal count achievements
 */
const checkMedalCountAchievement = async (): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }
    
    // Count medals from competition stats (RTDB, derived from placement rank)
    const stats = await getUserCompetitionStats(user.uid);
    const medalCount = stats.medals.gold + stats.medals.silver + stats.medals.bronze;
    
    // Determine achievement level based on medal count
    const countDef = getAchievementDefinition(AchievementType.MEDAL_COUNT);
    if (!countDef || !countDef.thresholds) {
      return null;
    }
    
    // Check if count qualifies for an achievement
    let achievementLevel: AchievementLevel | null = null;
    
    if (medalCount >= countDef.thresholds[AchievementLevel.PLATINUM]!) {
      achievementLevel = AchievementLevel.PLATINUM;
    } else if (medalCount >= countDef.thresholds[AchievementLevel.GOLD]!) {
      achievementLevel = AchievementLevel.GOLD;
    } else if (medalCount >= countDef.thresholds[AchievementLevel.SILVER]!) {
      achievementLevel = AchievementLevel.SILVER;
    } else if (medalCount >= countDef.thresholds[AchievementLevel.BRONZE]!) {
      achievementLevel = AchievementLevel.BRONZE;
    }
    
    if (achievementLevel) {
      return await createUserAchievement(
        AchievementType.MEDAL_COUNT,
        achievementLevel
      );
    }
    
    return null;
  } catch (error) {
    logger.error('Error checking medal count achievement:', error);
    return null;
  }
};

/**
 * Checks for gold medal count achievements
 */
const checkGoldMedalCountAchievement = async (): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }
    
    // Count gold medals from competition stats (RTDB, derived from rank === 1)
    const stats = await getUserCompetitionStats(user.uid);
    const goldMedalCount = stats.medals.gold;
    
    // Determine achievement level based on gold medal count
    const countDef = getAchievementDefinition(AchievementType.GOLD_MEDAL_COUNT);
    if (!countDef || !countDef.thresholds) {
      return null;
    }
    
    // Check if count qualifies for an achievement
    let achievementLevel: AchievementLevel | null = null;
    
    if (goldMedalCount >= countDef.thresholds[AchievementLevel.PLATINUM]!) {
      achievementLevel = AchievementLevel.PLATINUM;
    } else if (goldMedalCount >= countDef.thresholds[AchievementLevel.GOLD]!) {
      achievementLevel = AchievementLevel.GOLD;
    } else if (goldMedalCount >= countDef.thresholds[AchievementLevel.SILVER]!) {
      achievementLevel = AchievementLevel.SILVER;
    } else if (goldMedalCount >= countDef.thresholds[AchievementLevel.BRONZE]!) {
      achievementLevel = AchievementLevel.BRONZE;
    }
    
    if (achievementLevel) {
      return await createUserAchievement(
        AchievementType.GOLD_MEDAL_COUNT,
        achievementLevel
      );
    }
    
    return null;
  } catch (error) {
    logger.error('Error checking gold medal count achievement:', error);
    return null;
  }
};

/**
 * Checks for goal-related achievements
 * Triggered when a user creates or completes a goal
 */
export const checkGoalAchievements = async (isComplete: boolean = false): Promise<Achievement[]> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return [];
    }
    
    const achievements: Achievement[] = [];
    
    // Check for first goal achievement
    const firstGoal = await checkFirstGoalAchievement();
    if (firstGoal) {
      achievements.push(firstGoal);
    }
    
    // Check for completed goals achievement if this is a completed goal
    if (isComplete) {
      const completedGoals = await checkGoalsCompletedAchievement();
      if (completedGoals) {
        achievements.push(completedGoals);
      }
    }
    
    return achievements;
  } catch (error) {
    logger.error('Error checking goal achievements:', error);
    return [];
  }
};

/**
 * Checks if user has created their first goal
 */
const checkFirstGoalAchievement = async (): Promise<Achievement | null> => {
  try {
    // Check if user already has this achievement
    const alreadyHasAchievement = await hasAchievement(
      AchievementType.FIRST_GOAL,
      AchievementCategory.GOALS
    );
    
    if (alreadyHasAchievement) {
      return null;
    }
    
    // Create the achievement
    return await createUserAchievement(AchievementType.FIRST_GOAL);
  } catch (error) {
    logger.error('Error checking first goal achievement:', error);
    return null;
  }
};

/**
 * Checks for completed goals achievements
 */
const checkGoalsCompletedAchievement = async (): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }
    
    // Get count of user's completed goals (RTDB). getUserGoals returns raw
    // records typed with a leaner Goal shape, so read status defensively.
    const goals = await goalsService.getUserGoals(user.uid);
    const completedGoalsCount = goals.filter(
      goal => (goal as { status?: string }).status === 'completed'
    ).length;
    
    // Determine achievement level based on count
    const countDef = getAchievementDefinition(AchievementType.GOALS_COMPLETED);
    if (!countDef || !countDef.thresholds) {
      return null;
    }
    
    // Check if count qualifies for an achievement
    let achievementLevel: AchievementLevel | null = null;
    
    if (completedGoalsCount >= countDef.thresholds[AchievementLevel.PLATINUM]!) {
      achievementLevel = AchievementLevel.PLATINUM;
    } else if (completedGoalsCount >= countDef.thresholds[AchievementLevel.GOLD]!) {
      achievementLevel = AchievementLevel.GOLD;
    } else if (completedGoalsCount >= countDef.thresholds[AchievementLevel.SILVER]!) {
      achievementLevel = AchievementLevel.SILVER;
    } else if (completedGoalsCount >= countDef.thresholds[AchievementLevel.BRONZE]!) {
      achievementLevel = AchievementLevel.BRONZE;
    }
    
    if (achievementLevel) {
      return await createUserAchievement(
        AchievementType.GOALS_COMPLETED,
        achievementLevel
      );
    }
    
    return null;
  } catch (error) {
    logger.error('Error checking completed goals achievement:', error);
    return null;
  }
};

/**
 * Checks for profile completion achievement
 * Triggered when a user updates their profile
 */
export const checkProfileCompleteAchievement = async (): Promise<Achievement | null> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return null;
    }

    // Check if user already has this achievement
    const alreadyHasAchievement = await hasAchievement(
      AchievementType.PROFILE_COMPLETE,
      AchievementCategory.SPECIAL
    );

    if (alreadyHasAchievement) {
      return null;
    }

    // Get user's profile from Realtime Database (not Firestore)
    const { getData } = await import('./api-wrapper.service');
    const profile = await getData(`/users/${user.uid}`);

    if (!profile) {
      return null;
    }

    // Check if profile is complete (has all necessary fields)
    // Use photoURL/profilePhotoURL instead of 'photo' to match actual data structure
    const requiredFields = [
      'displayName',
      'bio',
      'photoURL',
      'martialArts'
    ];

    const isComplete = requiredFields.every(field => {
      // For photoURL, also check profilePhotoURL as fallback
      if (field === 'photoURL') {
        return (profile.photoURL || profile.profilePhotoURL) !== undefined &&
               (profile.photoURL || profile.profilePhotoURL) !== null &&
               (profile.photoURL || profile.profilePhotoURL) !== '';
      }
      return profile[field] !== undefined &&
             profile[field] !== null &&
             profile[field] !== '';
    });

    if (isComplete) {
      // Create the achievement
      return await createUserAchievement(AchievementType.PROFILE_COMPLETE);
    }

    return null;
  } catch (error) {
    logger.error('Error checking profile complete achievement:', error);
    return null;
  }
};

/**
 * Display a toast for each newly earned achievement via the global notification
 * bridge that AchievementNotificationProvider installs on `window`. Safe no-op
 * when the provider isn't mounted (e.g. background/import flows), so callers can
 * fire-and-forget after checkAchievements().
 * @param achievements Achievements returned by checkAchievements()
 */
export const displayEarnedAchievements = (achievements: Achievement[]): void => {
  if (!achievements || achievements.length === 0) {
    return;
  }
  const show = (window as any).__showAchievement;
  if (typeof show !== 'function') {
    return;
  }
  achievements.forEach(achievement => {
    try {
      show(achievement);
    } catch (error) {
      logger.warn('Failed to display earned achievement:', error);
    }
  });
};

/**
 * Check for achievements triggered by a specific action
 * @param action The action that triggered the check
 * @param data Data related to the action
 * @returns Array of achievements earned
 */
export const checkAchievements = async (
  action: 'training_added' | 'competition_added' | 'goal_created' | 'goal_completed' | 'profile_updated' | 'app_opened',
  data?: any
): Promise<Achievement[]> => {
  try {
    const achievements: Achievement[] = [];
    
    switch (action) {
      case 'training_added':
        // Check training-related achievements
        const firstTraining = await checkFirstTrainingAchievement();
        if (firstTraining) achievements.push(firstTraining);
        
        const trainingStreak = await checkTrainingStreakAchievement();
        if (trainingStreak) achievements.push(trainingStreak);
        
        const trainingCount = await checkTrainingCountAchievement();
        if (trainingCount) achievements.push(trainingCount);
        break;
        
      case 'competition_added':
        // Check competition-related achievements
        const competitionAchievements = await checkCompetitionAchievements();
        achievements.push(...competitionAchievements);
        break;
        
      case 'goal_created':
        // Check goal-related achievements for creation
        const goalCreationAchievements = await checkGoalAchievements(false);
        achievements.push(...goalCreationAchievements);
        break;
        
      case 'goal_completed':
        // Check goal-related achievements for completion
        const goalCompletionAchievements = await checkGoalAchievements(true);
        achievements.push(...goalCompletionAchievements);
        break;
        
      case 'profile_updated':
        // Check profile-related achievements
        const profileComplete = await checkProfileCompleteAchievement();
        if (profileComplete) achievements.push(profileComplete);
        break;
    }
    
    return achievements;
  } catch (error) {
    logger.error('Error checking achievements:', error);
    return [];
  }
};